import type {
  AdminOrgDto,
  AdminOrgListDto,
  AdminOrgLogoUploadDto,
  AdminOrgMemberCodeDto,
  AdminOrgMemberDto,
  AdminOrgMemberListDto,
  AdminOrgOwnerCodeDto,
  OrgContactsDto,
  OrgLogoContentType,
  OrgStatus,
  OrgStatusAction,
} from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks + writes for the admin «Компании» pages (AUT-403 /
 * CP-10b), against the AUT-396 `/admin/orgs*` API.
 *
 * Query keys: list → `['admin','orgs','list', params]`, detail →
 * `['admin','orgs','detail', id]`, members → `[…,'members', id, status]`,
 * member code → `[…,'member-code', id]`. Writes invalidate the prefix.
 *
 * **The owner code is never cached.** `issueOrgOwnerCode` is a plain call,
 * not a mutation: the plaintext exists only in that response, so the caller
 * keeps it in component state and drops it when the dialog closes. The
 * member code is re-derived by the server on every read, so caching it is
 * fine.
 *
 * Writes pass `skipAutoErrorToast`: the caller maps the error `code` to its
 * own toast (`companies/_lib/companyErrors`).
 *
 * The logo PUT itself is NOT here — it goes to S3 with plain `fetch`, never
 * through `apiClient` (see `pages/companies/_lib/logoUpload.ts`).
 */

const ORGS_BASE = '/admin/orgs';

export const ADMIN_ORGS_KEY = ['admin', 'orgs'] as const;

export const orgDetailKey = (id: string): readonly unknown[] => [...ADMIN_ORGS_KEY, 'detail', id];

export type OrgListStatusFilter = OrgStatus | 'all';
export type OrgMembersStatusFilter = 'active' | 'removed' | 'all';

export interface AdminOrgsListParams {
  status: OrgListStatusFilter;
  q?: string;
  cursor: string | null;
  limit: number;
}

const buildListParams = (params: AdminOrgsListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.status !== 'all') query.status = params.status;
  if (params.q) query.q = params.q;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminOrgsList = (
  params: AdminOrgsListParams,
): UseQueryResult<AdminOrgListDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_ORGS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminOrgListDto>(ORGS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminOrg = (id: string): UseQueryResult<AdminOrgDto, Error> =>
  useQuery({
    queryKey: orgDetailKey(id),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminOrgDto>(`${ORGS_BASE}/${id}`, { signal });
      return data;
    },
  });

export const useAdminOrgMembers = (
  id: string,
  status: OrgMembersStatusFilter,
): UseQueryResult<AdminOrgMemberListDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_ORGS_KEY, 'members', id, status],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminOrgMemberListDto>(`${ORGS_BASE}/${id}/members`, {
        params: { status },
        signal,
      });
      return data;
    },
  });

/** Off for a terminated company — its codes are revoked, the read would 409. */
export const useAdminOrgMemberCode = (
  id: string,
  enabled: boolean,
): UseQueryResult<AdminOrgMemberCodeDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_ORGS_KEY, 'member-code', id],
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminOrgMemberCodeDto>(
        `${ORGS_BASE}/${id}/member-code`,
        { signal },
      );
      return data;
    },
  });

/** `POST /admin/orgs` body — mirrors the API's `CreateOrgSchema`. */
export interface CreateOrgBody {
  name: string;
  legalName?: string;
  unp?: string;
  city?: string;
  /** AUT-761 — with `city` a dictionary key (or own text when `cityCustom`). */
  cityCountry?: string;
  cityCustom?: boolean;
  contacts: Partial<Record<keyof OrgContactsDto, string>>;
  maxMembers: number;
}

/** `PATCH /admin/orgs/:id` — only the changed keys; `null` clears. */
export interface UpdateOrgBody {
  name?: string;
  legalName?: string | null;
  unp?: string | null;
  city?: string | null;
  cityCountry?: string;
  cityCustom?: boolean;
  contacts?: Partial<Record<keyof OrgContactsDto, string | null>>;
  maxMembers?: number;
}

const quiet = { skipAutoErrorToast: true } as const;

export const createOrg = async (body: CreateOrgBody): Promise<AdminOrgDto> =>
  (await apiClient.post<AdminOrgDto>(ORGS_BASE, body, quiet)).data;

export const updateOrg = async (id: string, body: UpdateOrgBody): Promise<AdminOrgDto> =>
  (await apiClient.patch<AdminOrgDto>(`${ORGS_BASE}/${id}`, body, quiet)).data;

export const changeOrgStatus = async (
  id: string,
  action: OrgStatusAction,
  reason: string | undefined,
): Promise<AdminOrgDto> =>
  (
    await apiClient.post<AdminOrgDto>(
      `${ORGS_BASE}/${id}/status`,
      reason ? { action, reason } : { action },
      quiet,
    )
  ).data;

export const requestOrgLogoUpload = async (
  id: string,
  contentType: OrgLogoContentType,
): Promise<AdminOrgLogoUploadDto> =>
  (
    await apiClient.post<AdminOrgLogoUploadDto>(
      `${ORGS_BASE}/${id}/logo/upload-url`,
      { contentType },
      quiet,
    )
  ).data;

export const confirmOrgLogo = async (id: string, key: string): Promise<AdminOrgDto> =>
  (await apiClient.post<AdminOrgDto>(`${ORGS_BASE}/${id}/logo/confirm`, { key }, quiet)).data;

/** The plaintext exists ONLY in this response — keep it in component state. */
export const issueOrgOwnerCode = async (id: string): Promise<AdminOrgOwnerCodeDto> =>
  (await apiClient.post<AdminOrgOwnerCodeDto>(`${ORGS_BASE}/${id}/owner-code`, {}, quiet)).data;

export const rotateOrgMemberCode = async (id: string): Promise<AdminOrgMemberCodeDto> =>
  (await apiClient.post<AdminOrgMemberCodeDto>(`${ORGS_BASE}/${id}/member-code/rotate`, {}, quiet))
    .data;

export const removeOrgMember = async (id: string, userId: string): Promise<AdminOrgMemberDto> =>
  (await apiClient.delete<AdminOrgMemberDto>(`${ORGS_BASE}/${id}/members/${userId}`, quiet)).data;

export const useAdminOrgsCache = (): {
  patchDetail: (org: AdminOrgDto) => void;
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    patchDetail: (org) => queryClient.setQueryData(orgDetailKey(org.id), org),
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_ORGS_KEY }),
  };
};
